/** @jsxImportSource @emotion/react */

import { InfoOutlined } from '@mui/icons-material'
import { Box, Skeleton, Tooltip, Typography, useTheme } from '@mui/material'
import { useAtomValue } from 'jotai'
import { currencySymbolAtom } from '@app/state/clientsAtom'
import type { DamageCostReason } from '@app/hooks/useDamageCost'
import { formatDamageCostBand, reasonMessage } from './DamageCostStat.format'
import {
  damageCostStatInfoIcon,
  damageCostStatLabel,
  damageCostStatNotAvailable,
  damageCostStatRow,
  damageCostStatValue,
} from './DamageCostStat.styles'

export interface DamageStatProps {
  damage_cost_min: number | null | undefined
  damage_cost_max: number | null | undefined
  reason?: DamageCostReason
  isLoading?: boolean
  label?: string
  /** Optional currency override; otherwise pulled from `currencySymbolAtom`. */
  currencySymbol?: string
}

/**
 * Renders one of three states based on inputs:
 *   - Loading skeleton when `isLoading`.
 *   - Value band (e.g. `$1.2M – $2.4M`) when both bounds are finite.
 *   - "N/A" with an info-icon tooltip explaining the `reason`, otherwise.
 *
 * Currency comes from `currencySymbolAtom` unless overridden via prop.
 */
export const DamageCostStat = ({
  damage_cost_min,
  damage_cost_max,
  reason,
  isLoading,
  label = 'Damage cost',
  currencySymbol,
}: DamageStatProps) => {
  const theme = useTheme()
  const fallbackCurrency = useAtomValue(currencySymbolAtom)
  const symbol = currencySymbol ?? fallbackCurrency

  const hasBand =
    damage_cost_min != null &&
    damage_cost_max != null &&
    Number.isFinite(damage_cost_min) &&
    Number.isFinite(damage_cost_max)

  return (
    <Box css={damageCostStatRow({ theme })}>
      <Typography component="span" css={damageCostStatLabel}>
        {label}:
      </Typography>
      {isLoading ? (
        <Skeleton variant="text" width={120} />
      ) : hasBand ? (
        <Typography component="span" css={damageCostStatValue}>
          {formatDamageCostBand(damage_cost_min, damage_cost_max, symbol)}
        </Typography>
      ) : (
        <Typography component="span" css={damageCostStatNotAvailable({ theme })}>
          N/A
        </Typography>
      )}
      {!isLoading && (
        <Tooltip title={reasonMessage(reason)}>
          <InfoOutlined css={damageCostStatInfoIcon({ theme })} />
        </Tooltip>
      )}
    </Box>
  )
}

export default DamageCostStat
